import type { BadgeVariantProps } from './Badge.variants';

type Colour = NonNullable<BadgeVariantProps['colour']>;
type Size = NonNullable<BadgeVariantProps['size']>;
type Style = NonNullable<BadgeVariantProps['style']>;

/**
 * Icon size (px) per badge size for the `icon-label` type — XS 12 / S 14 /
 * M 14 / L 16 (Figma nodes 1752:3483 / 1752:3256 / 1749:6283 / 1752:1592).
 * Note it ramps SLOWER than the icon-only badge below. XXS 10 is code-side
 * (not in Figma yet — see `Badge.variants.ts`): the icon-only rule, since a
 * 12px icon beside an 11px label out-weighs it.
 */
export const BADGE_LABEL_ICON_SIZE_FOR: Record<Size, number> = {
  xxs: 10,
  xs: 12,
  s: 14,
  m: 14,
  l: 16,
};

/**
 * Icon size (px) per badge size for the `icon` (icon-only) type — exactly
 * `height / 2`: XS 12 / S 14 / M 16 / L 18 (Figma nodes 1752:3488 /
 * 1752:3261 / 1749:6288 / 1752:1299), and the code-side XXS 10.
 */
export const BADGE_ICON_ONLY_SIZE_FOR: Record<Size, number> = {
  xxs: 10,
  xs: 12,
  s: 14,
  m: 16,
  l: 18,
};

/**
 * Resolved icon colour per (colour, dark-mode). Mirrors `badgeTextVariants`.
 * Resolved to an rgb string because `lucide-react-native` / `react-native-svg`
 * accept `color` as a prop, not a className. On web the icon instead inherits
 * `currentColor` from the container's text colour (see `Badge.tsx`), so this
 * map is only consulted on native.
 *
 * Light = `-600` (matches the text); dark = the light `-400` hex — the value
 * the auto-inverting CSS-var palette resolves `*-600` to in dark mode
 * (confirmed against the Figma dark frame 2812:47477).
 */
const ICON_COLOR: Record<Colour, { light: string; dark: string }> = {
  // secondary-600 (#525252) / inverted → secondary-400 (#a1a1a1)
  neutral: { light: 'rgb(82, 82, 82)', dark: 'rgb(161, 161, 161)' },
  // primary-600 (#7f22fe) / inverted → primary-400 (#a684ff)
  primary: { light: 'rgb(127, 34, 254)', dark: 'rgb(166, 132, 255)' },
  // tertiary-600 (#155dfc) / inverted → tertiary-400 (#51a2ff)
  info: { light: 'rgb(21, 93, 252)', dark: 'rgb(81, 162, 255)' },
  // green-600 (#00a63e) / inverted → green-400 (#05df72)
  success: { light: 'rgb(0, 166, 62)', dark: 'rgb(5, 223, 114)' },
  // red-600 (#e7000b) / inverted → red-400 (#ff6467)
  error: { light: 'rgb(231, 0, 11)', dark: 'rgb(255, 100, 103)' },
  // orange-600 (#f54900) / inverted → orange-400 (#ff8904)
  warning: { light: 'rgb(245, 73, 0)', dark: 'rgb(255, 137, 4)' },
  // amber-600 (#e17100) / inverted → amber-400 (#ffb900)
  reward: { light: 'rgb(225, 113, 0)', dark: 'rgb(255, 185, 0)' },
  // Podium — the `-800` foreground on a solid medal fill, and its inverted
  // counterpart (see the Podium note in `Badge.variants.ts`).
  // deck-amber-800 (#973c00) / inverted (#fee685)
  gold: { light: 'rgb(151, 60, 0)', dark: 'rgb(254, 230, 133)' },
  // subtitle-800 (#262626) / inverted (#e5e5e5)
  silver: { light: 'rgb(38, 38, 38)', dark: 'rgb(229, 229, 229)' },
  // deck-orange-800 (#9f2d00) / inverted (#ffd6a8)
  bronze: { light: 'rgb(159, 45, 0)', dark: 'rgb(255, 214, 168)' },
};

/**
 * `style="solid"` foregrounds (mirrors the solid compounds in
 * `badgeTextVariants`): white on the `-500` fill in BOTH modes, and neutral's
 * `-50` on its `-800`, which inverts with it. Podium colours are absent — they
 * keep their filled look under `solid`, icon included.
 */
const WHITE = 'rgb(255, 255, 255)';
const SOLID_ICON_COLOR: Partial<Record<Colour, { light: string; dark: string }>> = {
  // secondary-50 (#ffffff) / inverted → secondary-950 (#0a0a0a)
  neutral: { light: WHITE, dark: 'rgb(10, 10, 10)' },
  primary: { light: WHITE, dark: WHITE },
  info: { light: WHITE, dark: WHITE },
  success: { light: WHITE, dark: WHITE },
  error: { light: WHITE, dark: WHITE },
  warning: { light: WHITE, dark: WHITE },
  reward: { light: WHITE, dark: WHITE },
};

export function resolveBadgeIconColor(
  colour: Colour,
  isDark: boolean,
  style: Style = 'filled',
): string {
  const pair = (style === 'solid' && SOLID_ICON_COLOR[colour]) || ICON_COLOR[colour];
  return isDark ? pair.dark : pair.light;
}
